import { useRef, useMemo, useState, useCallback, useContext, type MutableRefObject } from 'react';
import { standardizeLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { ISSUE_NAVIGATOR_ACTION_MODULE } from '@atlassian/jira-forge-ui-constants/src/constants.tsx';
import type { IssueNavigatorAction } from '@atlassian/jira-forge-ui-types/src/common/types/extension.tsx';
import { fetchModules } from '@atlassian/jira-forge-ui-utils/src/utils/fetch-modules/index.tsx';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { useActiveJql } from '@atlassian/jira-issue-navigator/src/services/active-jql/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { PluggableItem, PluggableItemsResponse } from '../../common/types.tsx';
import { PluggableItemsCacheContext } from '../../controllers/pluggable-items-cache/index.tsx';
import type { IssueNavigatorPluggableItemsQuery_jiraWebSection_webItems_nodes as ConnectPluggableItemType } from './__generated_apollo__/IssueNavigatorPluggableItemsQuery';
import { ISSUE_NAVIGATOR_PLUGGABLE_ITEMS_QUERY } from './gql.tsx';

type PluggableItemsFetcher = (forceRefetch?: boolean) => void;

export const usePluggableItemsQuery = (): [PluggableItemsFetcher, PluggableItemsResponse] => {
	let pluggableItems: PluggableItemsResponse;
	let setPluggableItems: (value: PluggableItemsResponse) => void;
	let lastKeyRef: MutableRefObject<string | undefined>;
	if (fg('empanada_nin_convert_apps_to_entrypoint')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const contextValue = useContext(PluggableItemsCacheContext);
		pluggableItems = contextValue.pluggableItems;
		setPluggableItems = contextValue.setPluggableItems;
		lastKeyRef = contextValue.lastKeyRef;
	} else {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		[pluggableItems, setPluggableItems] = useState<PluggableItemsResponse>({
			...pluggableItemsInitialState,
		});
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		lastKeyRef = useRef<string | undefined>(undefined);
	}

	const { jql, filterId } = useActiveJql();
	const cloudId = useCloudId();
	const isAnonymous = useIsAnonymous();
	const locale = useLocale();

	const key = !jql && isFilterId(filterId) ? filterId : jql;

	const currentKeyRef = useRef(key);
	const { current: intlCollator } = useRef(new Intl.Collator(standardizeLocale(locale)));

	const fetchPluggableItems: PluggableItemsFetcher = useCallback(
		(forceRefetch = false) => {
			if (
				forceRefetch ||
				lastKeyRef.current == null ||
				lastKeyRef.current !== currentKeyRef.current
			) {
				setPluggableItems({
					data: undefined,
					loading: true,
					error: false,
				});

				Promise.all([
					fetchModules({
						cloudId,
						isAnonymous,
						context: {},
						types: [ISSUE_NAVIGATOR_ACTION_MODULE],
						includeHidden: false,
						source: 'issue-navigator',
					}),
					performPostRequest('/rest/gira/1/?operation=IssueNavigatorPluggableItemsQuery', {
						body: JSON.stringify({
							query: ISSUE_NAVIGATOR_PLUGGABLE_ITEMS_QUERY,
							variables: { first: 100 },
						}),
					}),
				])
					.then(([forgeModules, connectResponse]) => {
						if (connectResponse.errors) {
							setPluggableItems({
								data: null,
								loading: false,
								error: true,
							});
						} else {
							setPluggableItems({
								data: transform(
									forgeModules[ISSUE_NAVIGATOR_ACTION_MODULE],
									connectResponse.data?.jiraWebSection?.webItems?.nodes,
									intlCollator.compare,
								),
								loading: false,
								error: false,
							});
						}
					})
					.catch(() => {
						setPluggableItems({
							data: null,
							loading: false,
							error: true,
						});
					});

				lastKeyRef.current = currentKeyRef.current;
			}
		},
		[cloudId, isAnonymous, intlCollator.compare, lastKeyRef, setPluggableItems],
	);

	// Sync this to avoid recreating the callback and mitigating rerender
	currentKeyRef.current = key;

	return useMemo(
		() => [fetchPluggableItems, pluggableItems],
		[pluggableItems, fetchPluggableItems],
	);
};

export const pluggableItemsInitialState: PluggableItemsResponse = {
	data: undefined,
	loading: false,
	error: false,
};

function transform(
	forgeModules: IssueNavigatorAction[],
	connectModules: ConnectPluggableItemType[],
	comparator: (a: string, b: string) => number,
): PluggableItem[] {
	return [
		...forgeModules.map((module) => {
			return {
				type: 'forge' as const,
				id: module.id,
				title: module.properties.title,
				iconUrl: module.properties.icon ?? null,
				tooltip: module.properties.tooltip ?? null,
				metadata: module,
			};
		}),
		...connectModules
			.filter((item) => item.label != null)
			.map((module) => ({
				type: 'connect' as const,
				id: module.id,
				title: module.label || '',
				iconUrl: module.iconUrl,
				tooltip: module.tooltip,
				metadata: module,
			})),
	].sort((a, b) => comparator(a.title, b.title));
}
